import { Stack } from "@mui/material";
import {
  AT_LEAST_ONE_NUMBER,
  AT_LEAST_ONE_SPECIAL_CHARACTER,
  PASSWORD_CASE_REGEX,
  PASSWORD_LENGTH_REGEX_8_50,
  PrimaryFormConfirmPassword,
  PrimaryFormPassword,
  PrimaryFormUserNameWithValidations,
  USER_NAME_REGEX,
} from "react-common-ui-library";
import { useNavigate } from "react-router-dom";
import {
  ACCOUNT_CREATION_ROUTE,
  EMPTY_STRING,
  SPACING_3_4,
} from "../../../Constants/CommonConstants";

import { isEmpty } from "lodash-es";
import { useCallback } from "react";
import { useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  CONFIRM_PASSWORD_FIELD_NAME,
  USERNAME_ALREADY_EXISTS,
  USER_NAME_FIELD_NAME,
} from "../../../Constants/AccountSetupConstants";
import {
  SLICE_ACCOUNT_CREATION,
  THUNK_POST_USERNAME,
} from "../../../Constants/SliceAndThunkConstants";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { manageUtilityDataSelector } from "../../../Selectors/UtilityDataSelector";
import { StyledAccountSetupStack } from "../../../Styles/AccountCreation/AccountSetup/AccountSetupStyles";
import AccountActionButtons from "../AccountActionButtons";
import {
  validatePasswordResponse,
  verifyUsernameResponse,
} from "./AccountSetupUtils";
import PropTypes from "prop-types";

/**
 * @description contains user name, password and confirm password form fields that are used to verify username and password and confirm password .
 * @param {function} method to handle for submission
 * @returns {ReactNode} Render the user name, password and confirm password form fields.
 */
const AccountSetupFields = ({ handleNextStep }) => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(
      state,
      SLICE_ACCOUNT_CREATION,
      THUNK_POST_USERNAME
    )
  );

  const { description } = apiResponses;

  const userNameValue = useWatch({
    name: USER_NAME_FIELD_NAME,
  });
  const confirmPasswordFieldValue = useWatch({
    name: CONFIRM_PASSWORD_FIELD_NAME,
  });

  const { isLoading } = useSelector((state) =>
    manageUtilityDataSelector(state)
  );

  const navigateToAccountHome = () => {
    navigate(`/${ACCOUNT_CREATION_ROUTE}`);
  };

  const userNameAsyncValidation = useCallback(
    async (value) => {
      if (USER_NAME_REGEX.test(value)) {
        return await verifyUsernameResponse(value, dispatch);
      }
      return true;
    },
    [dispatch]
  );

  const passwordAsyncValidation = useCallback(
    async (value) => {
      if (
        USER_NAME_REGEX.test(userNameValue) &&
        description !== USERNAME_ALREADY_EXISTS &&
        PASSWORD_LENGTH_REGEX_8_50.test(value) &&
        PASSWORD_CASE_REGEX.test(value) &&
        (AT_LEAST_ONE_NUMBER.test(value) ||
          AT_LEAST_ONE_SPECIAL_CHARACTER.test(value))
      ) {
        const { errorResponse, successResponse } =
          await validatePasswordResponse(value, dispatch, userNameValue);
        return { errorResponse, successResponse };
      }
      return { errorResponse: EMPTY_STRING, successResponse: [] };
    },
    [dispatch, userNameValue, description]
  );

  return (
    <Stack>
      <StyledAccountSetupStack>
        <Stack gap={SPACING_3_4}>
          <PrimaryFormUserNameWithValidations
            fieldProps={{
              userNameAsyncValidation: userNameAsyncValidation,
              disabled: isLoading,
            }}
          />
          <PrimaryFormPassword
            fieldProps={{
              passwordAsyncValidation: passwordAsyncValidation,
              disabled: isLoading,
              deps: [
                !isEmpty(confirmPasswordFieldValue)
                  ? CONFIRM_PASSWORD_FIELD_NAME
                  : EMPTY_STRING,
              ],
            }}
          />
        </Stack>
        <PrimaryFormConfirmPassword />
      </StyledAccountSetupStack>
      <AccountActionButtons
        cancelClickHandler={navigateToAccountHome}
        handleNextStep={handleNextStep}
      />
    </Stack>
  );
};

export default AccountSetupFields;

AccountSetupFields.propTypes = {
  handleNextStep: PropTypes.func.isRequired,
};
